
import { useState, useEffect, useCallback, useRef } from 'react';

export function useTimer(onTimeUpdate?: (time: number) => void) {
  const [time, setTime] = useState(60);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const startTimeRef = useRef<number | null>(null);
  const intervalIdRef = useRef<NodeJS.Timeout>();

  const stop = useCallback(() => {
    console.log('Stopping timer');
    setIsRunning(false);
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = undefined;
    }
  }, []);

  const updateTime = useCallback(() => {
    if (!startTimeRef.current) {
      console.warn('Timer update called without start time');
      return;
    }
    
    const now = Date.now();
    const elapsedMs = now - startTimeRef.current;
    const elapsedSeconds = Math.floor(elapsedMs / 1000);
    const remainingTime = Math.max(60 - elapsedSeconds, 0);
    
    console.log('Timer update:', { 
      elapsedSeconds, 
      elapsedMs,
      remainingTime,
      startTime: new Date(startTimeRef.current).toISOString(),
      currentTime: new Date(now).toISOString()
    });
    
    setTime(remainingTime);
    setElapsedTime(elapsedMs); // Store elapsed time in milliseconds
    
    if (onTimeUpdate) {
      onTimeUpdate(elapsedMs); // Pass elapsed time in milliseconds
    }
    
    if (remainingTime === 0) {
      console.log('Timer reached zero, stopping');
      stop();
    }
  }, [onTimeUpdate, stop]);

  const start = useCallback(() => {
    const now = Date.now();
    console.log('Starting timer with current time:', new Date(now).toISOString());
    
    if (isRunning) {
      console.warn('Timer already running, stopping previous instance');
      stop();
    }
    
    startTimeRef.current = now;
    setIsRunning(true);
  }, [isRunning, stop]);

  const reset = useCallback(() => {
    console.log('Resetting timer');
    stop();
    startTimeRef.current = null;
    setTime(60);
    setElapsedTime(0);
  }, [stop]);

  // Handle interval setup and cleanup
  useEffect(() => {
    if (isRunning) {
      console.log('Setting up interval');
      
      // Cleanup any existing interval first
      if (intervalIdRef.current) {
        console.warn('Clearing existing interval before setting new one');
        clearInterval(intervalIdRef.current);
      }
      
      // Set up new interval and do initial update
      intervalIdRef.current = setInterval(updateTime, 1000);
      updateTime();
    }

    // Cleanup function
    return () => {
      if (intervalIdRef.current) {
        console.log('Cleaning up interval on unmount/dependency change');
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = undefined;
      }
    };
  }, [isRunning, updateTime]);

  // Final cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('Component unmounting, cleaning up timer');
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = undefined;
      }
      startTimeRef.current = null;
    };
  }, []);

  return {
    time,
    start,
    stop,
    reset,
    isRunning,
    elapsedTime,
    setElapsedTime
  };
}
